import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { getComments, createComment, updateComment, deleteComment } from '../services/api';

const AdminComments = () => {
  const history = useHistory();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBy, setSearchBy] = useState('content');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [currentComment, setCurrentComment] = useState(null);
  const [formData, setFormData] = useState({
    dramaId: '',
    userId: '',
    content: '',
    userIP: '',
  });

  // 获取评论数据
  const fetchComments = async () => {
    try {
      setLoading(true);
      const data = await getComments();
      setComments(data);
    } catch (err) {
      setError('获取评论数据失败');
      console.error('获取评论数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, []);

  // 搜索处理
  const filteredComments = comments.filter(comment => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    switch (searchBy) {
      case 'content':
        return comment.content.toLowerCase().includes(term);
      case 'dramaId':
        return comment.dramaId.toLowerCase().includes(term);
      case 'userId':
        return comment.userId.toLowerCase().includes(term);
      case 'userIP':
        return comment.userIP.toLowerCase().includes(term);
      default:
        return true;
    }
  });

  // 分页处理
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 对话框处理
  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setCurrentComment(null);
    setFormData({
      dramaId: '',
      userId: '',
      content: '',
      userIP: '',
    });
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (comment) => {
    setDialogMode('edit');
    setCurrentComment(comment);
    setFormData({
      dramaId: comment.dramaId,
      userId: comment.userId,
      content: comment.content,
      userIP: comment.userIP,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // 表单处理
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (dialogMode === 'create') {
        await createComment(formData);
        setSuccess('评论创建成功');
      } else {
        await updateComment(currentComment.id, formData);
        setSuccess('评论更新成功');
      }
      
      handleCloseDialog();
      fetchComments();
    } catch (err) {
      setError(dialogMode === 'create' ? '创建评论失败' : '更新评论失败');
      console.error('提交评论失败:', err);
    }
  };

  // 删除处理
  const handleDelete = async (commentId) => {
    if (window.confirm('确定要删除这个评论吗？')) {
      try {
        await deleteComment(commentId);
        setSuccess('评论删除成功');
        fetchComments();
      } catch (err) {
        setError('删除评论失败');
        console.error('删除评论失败:', err);
      }
    }
  };

  // 刷新数据
  const handleRefresh = () => {
    fetchComments();
  };

  // 格式化日期
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <Container maxWidth="lg">


      {/* 操作栏 */}
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          {/* 搜索区域 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="搜索"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ minWidth: 200 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>搜索字段</InputLabel>
              <Select
                value={searchBy}
                label="搜索字段"
                onChange={(e) => setSearchBy(e.target.value)}
              >
                <MenuItem value="content">内容</MenuItem>
                <MenuItem value="dramaId">剧集ID</MenuItem>
                <MenuItem value="userId">用户ID</MenuItem>
                <MenuItem value="userIP">用户IP</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* 操作按钮 */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
            >
              添加评论
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* 评论列表 */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>剧集ID</TableCell>
                <TableCell>用户IP</TableCell>
                <TableCell>内容</TableCell>
                <TableCell>评论时间</TableCell>
                <TableCell>创建时间</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    加载中...
                  </TableCell>
                </TableRow>
              ) : filteredComments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    没有找到符合条件的评论
                  </TableCell>
                </TableRow>
              ) : (
                filteredComments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((comment) => (
                  <TableRow key={comment.id}>
                    <TableCell sx={{ maxWidth: 150 }}>
                      <Chip label={comment.dramaId.substring(0, 8)} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{comment.userIP}</TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap>
                        {comment.content}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(comment.commentTime)}</TableCell>
                    <TableCell>{formatDate(comment.createdAt)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEditDialog(comment)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(comment.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 分页 */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredComments.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* 创建/编辑对话框 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? '添加评论' : '编辑评论'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="剧集ID"
              name="dramaId"
              value={formData.dramaId}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="用户ID"
              name="userId"
              value={formData.userId}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="用户IP"
              name="userIP"
              value={formData.userIP}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="评论内容"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={4}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>取消</Button>
            <Button type="submit" variant="contained" color="primary">
              {dialogMode === 'create' ? '创建' : '更新'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* 通知 */}
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminComments;